<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import Modal from "./Modal.vue";
import {
  PAUSE_PERIODS,
  activeMessagesApi,
  dayLabel,
  type ActiveHistoryItem,
  type ActiveMessages,
  type PausePeriod,
} from "../api/activeMessages";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { locale, t } from "../i18n";

/**
 * "Mensagens ativas" on the contact panel (decision 0036, B5): whether the company may start a
 * conversation, paused until a day or refused, where that came from, and the changes a person can
 * make. The contact is answered normally when they write, whatever the state.
 */
const props = defineProps<{
  slug: string;
  contactId: string;
  canEdit: boolean;
  /** For the styleguide: a state to show without asking the API. */
  sample?: ActiveMessages | null;
}>();
const emit = defineEmits<{ changed: [state: ActiveMessages] }>();

const state = ref<ActiveMessages | null>(props.sample ?? null);
const busy = ref(false);
async function load() {
  if (props.sample) return;
  try {
    state.value = await activeMessagesApi.get(props.slug, props.contactId);
  } catch {
    state.value = null;
  }
}
watch(() => props.contactId, load, { immediate: true });

const day = (d: string) => dayLabel(d, locale.value);
const when = (iso: string) =>
  new Date(iso).toLocaleDateString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

/** "tocou “Agora não” no modelo imovel_ainda_disponivel", "pediu na conversa"… */
function origin(item: {
  source: string | null;
  evidence: ActiveHistoryItem["evidence"];
}): string {
  const e = item.evidence ?? {};
  switch (item.source) {
    case "button":
      return t("active.origin.button", {
        button: String(e.text ?? ""),
        template: String(e.template ?? ""),
      });
    case "assistant":
      return t("active.origin.assistant");
    case "keyword":
      return t("active.origin.keyword", { word: String(e.keyword ?? "") });
    case "api":
      return t("active.origin.api");
    case "pause_ended":
      return t("active.origin.pause_ended");
    case "flow":
      return t("active.origin.flow");
    default:
      return t("active.origin.person");
  }
}

const headline = computed(() => {
  const s = state.value;
  if (!s) return "";
  if (s.status === "paused" && s.until)
    return t("active.state.paused", { day: day(s.until) });
  if (s.status === "opted_out") return t("active.state.opted_out");
  return t("active.state.can_receive");
});

async function change(body: Parameters<typeof activeMessagesApi.set>[2]) {
  busy.value = true;
  try {
    state.value = await activeMessagesApi.set(
      props.slug,
      props.contactId,
      body,
    );
    emit("changed", state.value);
    pausing.value = false;
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

/** "Pausar até…": a period from today or a day. */
const pausing = ref(false);
const period = ref<PausePeriod | null>("3m");
const date = ref("");
function openPause() {
  period.value = state.value?.until ? null : "3m";
  date.value = state.value?.until ?? "";
  pausing.value = true;
}
const today = new Date().toISOString().slice(0, 10);
const yearAhead = new Date(Date.now() + 366 * 86_400_000)
  .toISOString()
  .slice(0, 10);
function confirmPause() {
  if (period.value) void change({ status: "paused", period: period.value });
  else if (date.value) void change({ status: "paused", until: date.value });
}
</script>
<template>
  <div v-if="state" class="active-card" :class="`active-${state.status}`">
    <div class="active-card-head">
      <Icon
        :name="
          state.status === 'can_receive'
            ? 'checkCircle'
            : state.status === 'paused'
              ? 'pause'
              : 'ban'
        "
        :size="18"
      />
      <strong>{{ headline }}</strong>
    </div>
    <p v-if="state.status !== 'can_receive' && state.since" class="small">
      {{ t("active.since", { day: when(state.since), how: origin(state) }) }}
    </p>
    <p class="small muted">{{ t("active.answered") }}</p>
    <div v-if="canEdit" class="active-card-actions">
      <template v-if="state.status === 'paused'">
        <Button
          compact
          variant="secondary"
          :disabled="busy"
          @click="openPause"
          >{{ t("active.action.change_date") }}</Button
        >
        <Button
          compact
          variant="secondary"
          :loading="busy"
          @click="change({ status: 'can_receive' })"
          >{{ t("active.action.end_pause") }}</Button
        >
      </template>
      <Button
        v-else-if="state.status === 'opted_out'"
        compact
        variant="secondary"
        :loading="busy"
        @click="change({ status: 'can_receive' })"
        >{{ t("active.action.resume") }}</Button
      >
      <template v-else>
        <Button
          compact
          variant="secondary"
          :disabled="busy"
          @click="openPause"
          >{{ t("active.action.pause") }}</Button
        >
        <Button
          compact
          variant="secondary"
          :loading="busy"
          @click="change({ status: 'opted_out' })"
          >{{ t("active.action.opt_out") }}</Button
        >
      </template>
    </div>
    <details v-if="state.history.length" class="active-history">
      <summary class="small">{{ t("active.history") }}</summary>
      <ul class="small">
        <li v-for="(h, i) in state.history" :key="i">
          {{ when(h.at) }} ·
          {{
            h.status === "paused" && h.until
              ? t("active.history_paused", { day: day(h.until) })
              : t(`active.status.${h.status}`)
          }}
          · {{ origin(h) }}
        </li>
      </ul>
    </details>

    <Modal
      :open="pausing"
      :title="t('active.pause.title')"
      @close="pausing = false"
    >
      <div class="active-pause">
        <p class="small muted">{{ t("active.pause.hint") }}</p>
        <div
          class="button-action-periods"
          role="group"
          :aria-label="t('active.pause.for')"
        >
          <button
            v-for="p in PAUSE_PERIODS"
            :key="p"
            type="button"
            class="chip"
            :class="{ active: period === p }"
            :aria-pressed="period === p"
            @click="period = p"
          >
            {{ t(`active.period.${p}`) }}
          </button>
        </div>
        <label class="wa-field"
          ><span>{{ t("active.pause.until") }}</span
          ><input
            v-model="date"
            type="date"
            :min="today"
            :max="yearAhead"
            @input="period = null"
        /></label>
      </div>
      <template #footer>
        <Button variant="secondary" @click="pausing = false">{{
          t("common.cancel")
        }}</Button>
        <Button
          :loading="busy"
          :disabled="!period && !date"
          @click="confirmPause"
          >{{ t("active.pause.save") }}</Button
        >
      </template>
    </Modal>
  </div>
</template>
